import { api } from "./api";
import type {
  AcceptedDocumentType,
  Booking,
  BookingPayment,
  CreateBookingPayload,
  DateChangePreview,
  RefundQuote,
  CreatePropertyPayload,
  CreateListingPayload,
  CreateReviewPayload,
  HostApplication,
  Listing,
  ListingBlock,
  LoginPayload,
  Notification,
  PaginatedResponse,
  PaymentSummary,
  Property,
  RegisterPayload,
  Reservation,
  Review,
  SingleResponse,
  UpdateUserPayload,
  UpdateReviewPayload,
  User,
  PropertyTypeOption,
  RoomTypeOption,
} from "@/types/api";

// ---------- Auth ----------
export interface AuthResponse {
  user: User;
  access_token: string;
  token_type: "Bearer";
  can_access_host_dashboard?: boolean;
}

export const authService = {
  register: (payload: RegisterPayload) =>
    api.post<AuthResponse>("/register", payload).then((r) => r.data),
  login: (payload: LoginPayload) => api.post<AuthResponse>("/login", payload).then((r) => r.data),
  me: () => api.get<User>("/user").then((r) => r.data),
  /** Partial update. Returns the same shape as `me()`, so callers can swap their cached user. */
  updateProfile: (payload: UpdateUserPayload) =>
    api.patch<User>("/user", payload).then((r) => r.data),
  removeAvatar: () => api.delete<User>("/user/avatar").then((r) => r.data),
  logout: () => api.post<{ message: string }>("/logout", {}).then((r) => r.data),
};

// ---------- Properties ----------
export interface PropertyListParams {
  search?: string;
  lat?: number;
  lng?: number;
  radius?: number;
  /** Map viewport corners — return what is on screen, nothing more. */
  ne_lat?: number;
  ne_lng?: number;
  sw_lat?: number;
  sw_lng?: number;
  property_type?: string;
  /** Skip properties with nothing bookable on them. */
  has_listings?: boolean;
  /** Embed each property's active listings instead of fetching them one by one. */
  with_listings?: boolean;
  per_page?: number;
  page?: number;
}

/*
  The stays vocabularies. Fetched once and held: they change when the platform
  adds a property type, not while someone is browsing.
*/
export const stayVocabularyService = {
  get: (signal?: AbortSignal) =>
    api
      .get<{ property_types: PropertyTypeOption[]; room_types: RoomTypeOption[] }>(
        "/stay-vocabulary",
        { signal },
      )
      .then((r) => r.data),
};

export const propertyService = {
  // The AbortSignal lets React Query cancel a request the moment its query key
  // changes. Without it, a guest sweeping through filters leaves a queue of
  // superseded requests competing for the connection.
  list: (params?: PropertyListParams, signal?: AbortSignal) =>
    api.get<PaginatedResponse<Property>>("/properties", { params, signal }).then((r) => r.data),
  get: (id: string) => api.get<SingleResponse<Property>>(`/properties/${id}`).then((r) => r.data),
  create: (payload: CreatePropertyPayload) =>
    api.post<SingleResponse<Property>>("/properties", payload).then((r) => r.data),
  update: (id: string, payload: Partial<CreatePropertyPayload>) =>
    api.put<SingleResponse<Property>>(`/properties/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete<{ message: string }>(`/properties/${id}`).then((r) => r.data),
};

// ---------- Listings ----------
/**
 * The discovery query, in full.
 *
 * Every constraint a guest can express reaches the server. Nothing in this
 * list is re-applied in the browser afterwards: a page that the client thins
 * out is a page whose result count, "load more" and empty state are all wrong.
 */
export interface ListingListParams {
  search?: string;
  minPrice?: number;
  maxPrice?: number;
  amenities?: string;
  /** Minimum capacity — "sleeps this many or more". */
  guests?: number;
  bedrooms?: number;
  bathrooms?: number;
  instant_book?: boolean;
  allows_pets?: boolean;
  self_check_in?: boolean;
  /** Availability window, `YYYY-MM-DD`. Both or neither. */
  check_in?: string;
  check_out?: string;
  /** Property type of the parent property. */
  property_type?: string;
  /** Entire place, private room or shared room. */
  room_type?: string;
  property_id?: string;
  verified_only?: boolean;
  /** Proximity search. */
  lat?: number;
  lng?: number;
  radius?: number;
  /** Map viewport corners; takes precedence over lat/lng/radius. */
  ne_lat?: number;
  ne_lng?: number;
  sw_lat?: number;
  sw_lng?: number;
  sort?: "recommended" | "price_asc" | "price_desc" | "rating" | "newest";
  per_page?: number;
  page?: number;
}

/** Window for a listing's reservation calendar. */
export interface ReservationWindowParams {
  from?: string;
  until?: string;
}

export const listingService = {
  list: (params?: ListingListParams, signal?: AbortSignal) =>
    api.get<PaginatedResponse<Listing>>("/listings", { params, signal }).then((r) => r.data),
  get: (id: string, signal?: AbortSignal) =>
    api.get<SingleResponse<Listing>>(`/listings/${id}`, { signal }).then((r) => r.data),
  reservations: (id: string, params?: ReservationWindowParams) =>
    api
      .get<{ data: Reservation[] }>(`/listings/${id}/reservations`, { params })
      .then((r) => r.data),
  reviews: (id: string) =>
    api.get<PaginatedResponse<Review>>(`/listings/${id}/reviews`).then((r) => r.data),
  create: (payload: CreateListingPayload) =>
    api.post<SingleResponse<Listing>>("/listings", payload).then((r) => r.data),
  update: (id: string, payload: Partial<CreateListingPayload>) =>
    api.put<SingleResponse<Listing>>(`/listings/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete<{ message: string }>(`/listings/${id}`).then((r) => r.data),
};

// ---------- Bookings ----------
export interface GuestBookingParams {
  status?: string;
  upcoming?: boolean;
  past?: boolean;
  per_page?: number;
}

export const bookingService = {
  create: (payload: CreateBookingPayload) =>
    api.post<SingleResponse<Booking>>("/guest/bookings", payload).then((r) => r.data),
  guestBookings: (params?: GuestBookingParams) =>
    api.get<PaginatedResponse<Booking>>("/guest/bookings", { params }).then((r) => r.data),
  get: (id: string) => api.get<SingleResponse<Booking>>(`/bookings/${id}`).then((r) => r.data),

  /**
   * What cancelling would return, asked before anything is changed.
   *
   * The same computation the cancellation itself runs, so the figure shown in
   * a confirmation dialog is the figure that gets recorded.
   */
  cancellationQuote: (id: string) =>
    api.get<{ data: RefundQuote }>(`/bookings/${id}/cancellation-quote`).then((r) => r.data.data),

  cancel: (id: string, reason?: string) =>
    api
      .delete<{ message: string; data: Booking; refund: RefundQuote }>(`/bookings/${id}`, {
        data: reason ? { reason } : {},
      })
      .then((r) => r.data),

  /** Availability and price for proposed dates, without applying them. */
  previewDateChange: (id: string, checkIn: string, checkOut: string) =>
    api
      .post<{ data: DateChangePreview }>(`/bookings/${id}/date-change-preview`, {
        check_in_date: checkIn,
        check_out_date: checkOut,
      })
      .then((r) => r.data.data),

  changeDates: (id: string, checkIn: string, checkOut: string, reason?: string) =>
    api
      .patch<SingleResponse<Booking>>(`/bookings/${id}/dates`, {
        check_in_date: checkIn,
        check_out_date: checkOut,
        ...(reason ? { reason } : {}),
      })
      .then((r) => r.data),
};

// ---------- Payments ----------
export interface RecordPaymentPayload {
  type: "charge" | "refund";
  amount: number;
  method?: string;
  reference?: string;
  note?: string;
}

/**
 * The money ledger for a booking.
 *
 * Not a gateway. Recording a payment asserts that money moved somewhere the
 * system cannot see — cash, mobile money, a transfer — which is what the
 * platform needs until a provider is wired in.
 */
export const paymentService = {
  list: (bookingId: string) =>
    api
      .get<{ data: BookingPayment[]; summary: PaymentSummary }>(`/bookings/${bookingId}/payments`)
      .then((r) => r.data),
  record: (bookingId: string, payload: RecordPaymentPayload) =>
    api
      .post<{ data: BookingPayment; summary: PaymentSummary }>(
        `/bookings/${bookingId}/payments`,
        payload,
      )
      .then((r) => r.data),
  void: (bookingId: string, paymentId: string, reason: string) =>
    api
      .delete<{ message: string; summary: PaymentSummary }>(
        `/bookings/${bookingId}/payments/${paymentId}`,
        { data: { reason } },
      )
      .then((r) => r.data),
};

// ---------- Reviews ----------
export const reviewService = {
  byProperty: (propertyId: string) =>
    api
      .get<PaginatedResponse<Review>>("/reviews", { params: { property_id: propertyId } })
      .then((r) => r.data),
  byListing: (listingId: string) =>
    api.get<PaginatedResponse<Review>>(`/listings/${listingId}/reviews`).then((r) => r.data),
  create: (payload: CreateReviewPayload) =>
    api.post<SingleResponse<Review>>("/reviews", payload).then((r) => r.data),
  update: (id: string, payload: UpdateReviewPayload) =>
    api.put<SingleResponse<Review>>(`/reviews/${id}`, payload).then((r) => r.data),
  remove: (id: string) => api.delete<{ message: string }>(`/reviews/${id}`).then((r) => r.data),
};

// ---------- Notifications ----------
export interface NotificationsListResponse {
  notifications: Notification[];
  unread_count: number;
  pagination: { current_page: number; last_page: number; per_page: number; total: number };
}

export const notificationService = {
  list: (perPage = 20) =>
    api
      .get<NotificationsListResponse>("/notifications", { params: { per_page: perPage } })
      .then((r) => r.data),
  count: () => api.get<{ unread_count: number }>("/notifications/count").then((r) => r.data),
  markRead: (id: string) => api.post(`/notifications/${id}/read`, {}).then((r) => r.data),
  markAllRead: () =>
    api.post<{ message: string }>("/notifications/read-all", {}).then((r) => r.data),
  remove: (id: string) =>
    api.delete<{ message: string }>(`/notifications/${id}`).then((r) => r.data),
};

// ---------- Becoming a host ----------

export interface SaveHostApplicationPayload {
  legal_name: string;
  phone: string;
  residential_address: string;
  national_id_number?: string;
  business_name?: string;
  about?: string;
}

/**
 * The caller's own application to become a host.
 *
 * No id appears in any of these paths: a person has exactly one application and
 * it is always theirs, so there is nothing to address.
 */
export const hostApplicationService = {
  get: () =>
    api
      .get<{ data: HostApplication | null; accepted_documents: AcceptedDocumentType[] }>(
        "/host-application",
      )
      .then((r) => r.data),

  save: (payload: SaveHostApplicationPayload) =>
    api
      .post<{ data: HostApplication; message: string }>("/host-application", payload)
      .then((r) => r.data),

  uploadDocument: (type: string, file: File) => {
    const body = new FormData();
    body.append("type", type);
    body.append("document", file, file.name);

    return api
      .post<{ application: HostApplication }>("/host-application/documents", body)
      .then((r) => r.data);
  },

  removeDocument: (documentId: string) =>
    api
      .delete<{ application: HostApplication }>(`/host-application/documents/${documentId}`)
      .then((r) => r.data),

  submit: () =>
    api
      .post<{ data: HostApplication; message: string }>("/host-application/submit", {})
      .then((r) => r.data),
};

// ---------- Host Dashboard ----------
export interface HostBookingParams {
  status?: string;
  per_page?: number;
  page?: number;
}

export interface HostManualBookingPayload {
  listing_id: string;
  check_in_date: string;
  check_out_date: string;
  guest_count: number;
  guest_name: string;
  guest_phone?: string;
  guest_email?: string;
  notes?: string;
}

export interface HostBlockDatesPayload {
  listing_id: string;
  check_in_date: string;
  check_out_date: string;
  reason?: string;
}

export const hostService = {
  properties: (search?: string) =>
    api
      .get<PaginatedResponse<Property> | { data: Property[] }>("/host/properties", {
        params: search ? { search } : undefined,
      })
      .then((r) => r.data),
  listings: () =>
    api.get<PaginatedResponse<Listing> | { data: Listing[] }>("/host/listings").then((r) => r.data),
  bookings: (params?: HostBookingParams) =>
    api.get<PaginatedResponse<Booking>>("/host/bookings", { params }).then((r) => r.data),
  confirmBooking: (id: string) =>
    api.patch<{ message: string }>(`/host/bookings/${id}/confirm`, {}).then((r) => r.data),
  rejectBooking: (id: string, reason?: string) =>
    api
      .patch<{ message: string }>(`/host/bookings/${id}/reject`, reason ? { reason } : {})
      .then((r) => r.data),
  cancelBooking: (id: string, reason?: string) =>
    api
      .patch<{ message: string; data: Booking; refund: RefundQuote }>(
        `/host/bookings/${id}/cancel`,
        reason ? { reason } : {},
      )
      .then((r) => r.data),
  /** Reschedule a booking on the host's own property. */
  changeBookingDates: (id: string, checkIn: string, checkOut: string, reason?: string) =>
    api
      .patch<SingleResponse<Booking>>(`/host/bookings/${id}/dates`, {
        check_in_date: checkIn,
        check_out_date: checkOut,
        ...(reason ? { reason } : {}),
      })
      .then((r) => r.data),
  updateBooking: (
    id: string,
    payload: Partial<Pick<Booking, "check_in_date" | "check_out_date" | "guest_count" | "notes">>,
  ) => api.put<SingleResponse<Booking>>(`/host/bookings/${id}`, payload).then((r) => r.data),
  createManualBooking: (payload: HostManualBookingPayload) =>
    api.post<SingleResponse<Booking>>("/host/bookings", payload).then((r) => r.data),
  blockDates: (payload: HostBlockDatesPayload) =>
    api
      .post<SingleResponse<ListingBlock>>(`/host/listings/${payload.listing_id}/blocks`, {
        check_in_date: payload.check_in_date,
        check_out_date: payload.check_out_date,
        reason: payload.reason,
      })
      .then((r) => r.data),
  unblockDates: (blockId: string) =>
    api.delete<{ message: string }>(`/host/listings/blocks/${blockId}`).then((r) => r.data),
  /** Every block across the host's listings, within an optional window. */
  blocks: (params?: { from?: string; until?: string }) =>
    api.get<{ data: ListingBlock[] }>("/host/listings/blocks", { params }).then((r) => r.data),
  reservations: (month?: number, year?: number) =>
    api
      .get<{ data: Reservation[] }>("/host/listings/reservations", {
        params: { month, year },
      })
      .then((r) => r.data),
};

// ---------- Firebase Auth Bridge ----------
export const firebaseAuthService = {
  customToken: () =>
    api
      .get<{ token: string; uid: string; expires_in: number }>("/firebase/auth")
      .then((r) => r.data),
  revoke: () => api.post<{ message: string }>("/firebase/auth/revoke", {}).then((r) => r.data),
};
